import { useCallback, useEffect, useRef, useState } from 'react';
import { getProjects } from '@src/apis/project';
import { toCardProject } from '@src/services/project';

// Each view has its own copy of this hook, so switching views reloads the list.
// `append` on: the grid adds each page to the ones before. Off: the list
// replaces them.
const useRecentProjects = ({
  search,
  defaultRowsPerPage,
  append,
  removedIds,
}) => {
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  // A short page means the end. Without it a wrong `total` would keep the grid
  // asking for more forever.
  const reachedEnd = useRef(false);

  // Bump to fetch again. `quiet` keeps `loading` off, so nothing on screen moves.
  const [refreshKey, setRefreshKey] = useState(0);
  const quiet = useRef(false);

  // Cards a delete took out, so the refetch asks for exactly that many.
  const gap = useRef(0);

  // Where the next batch starts. `page * rowsPerPage` overshoots after a delete.
  const loaded = useRef(0);
  useEffect(() => {
    loaded.current = projects.length;
  }, [projects]);

  // A new keyword starts at page 1.
  useEffect(() => {
    setPage(0);
    reachedEnd.current = false;
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    // Page 0 with no gap is a fresh start, so a new keyword replaces the rest.
    const appending = append && (page > 0 || gap.current > 0);

    const fetchProjects = async () => {
      const silent = quiet.current;
      // Replacing always takes a whole page, so only appending uses the gap.
      const limit = (appending && gap.current) || rowsPerPage;
      quiet.current = false;
      gap.current = 0;
      if (!silent) setLoading(true);
      try {
        const response = await getProjects(
          {
            search,
            offset: appending ? loaded.current : page * rowsPerPage,
            limit,
            sort: 'updatedAt_desc',
          },
          { signal: controller.signal },
        );

        if (response?.status) {
          const raw = response.result?.projects || [];
          // Narrowed before it reaches state, so the response's `blocks` and
          // `music_blocks` are collectable rather than held for the life of the
          // page — see toCardProject.
          const items = raw.map(toCardProject);
          reachedEnd.current = items.length < limit;
          setProjects((prev) => (appending ? [...prev, ...items] : items));
          setTotal(response.result?.metadata?.total || 0);
        }
      } catch {
        // Aborted, or the api layer rejected — either way keep the list as is.
      } finally {
        // A superseded request must not switch the spinner off under the new one.
        if (!controller.signal.aborted && !silent) setLoading(false);
      }
    };

    fetchProjects();
    // Drops the connection instead of downloading a response nobody needs.
    return () => controller.abort();
  }, [search, page, rowsPerPage, append, refreshKey]);

  // Drop deleted rows here instead of refetching, so the user waits for nothing.
  // The ref skips the value already there at mount: a fresh fetch has left those
  // rows out, and subtracting them again would put `total` below the truth.
  const handledRemoval = useRef(removedIds);
  useEffect(() => {
    if (removedIds === handledRemoval.current || !removedIds?.length) return;
    handledRemoval.current = removedIds;

    const gone = new Set(removedIds);
    const remaining = projects.filter((project) => !gone.has(project.id));
    setProjects(remaining);
    setTotal((prev) => Math.max(0, prev - removedIds.length));

    // Pull up what moved in behind. An emptied page is the clamp's job below.
    if (!remaining.length) return;
    if (append) gap.current = projects.length - remaining.length;
    quiet.current = true;
    setRefreshKey((key) => key + 1);
  }, [removedIds]);

  // Deleting a whole page leaves it empty. Jump straight to the last page that
  // still has rows — stepping back one at a time overshoots, because `loading`
  // is a render behind and the condition is still true.
  const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
  useEffect(() => {
    if (!loading && page > lastPage) setPage(lastPage);
  }, [loading, page, lastPage]);

  // useCallback so the grid's observer is not rebuilt every render.
  const loadMore = useCallback(() => setPage((prev) => prev + 1), []);

  return {
    projects,
    total,
    loading,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    hasMore: !reachedEnd.current && projects.length < total,
    loadMore,
  };
};

export default useRecentProjects;
